import { useMutation, useQuery } from "@tanstack/react-query"
import {
  ClientOnly,
  createFileRoute,
  useStableCallback,
} from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  type ColumnDef,
  createColumnHelper,
  type VisibilityState,
} from "@tanstack/react-table"
import { PencilIcon, PlusIcon, PowerIcon } from "lucide-react"
import { useId, useMemo, useState } from "react"

import {
  ActionsDropdownMenu,
  type ActionsDropdownMenuGroup,
} from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DataTable } from "@repo/ui/components/ui/data-table"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { toast } from "@repo/ui/components/ui/sonner"
import { useLocalStorage } from "@repo/ui/hooks/use-local-storage"
import { AddSystemInvoiceItemModal } from "~/modules/system-config/components/add-invoice-item-modal"
import { EditSystemInvoiceItemModal } from "~/modules/system-config/components/edit-invoice-item-modal"
import { updateSystemInvoiceItemStatusFn } from "~/modules/system-config/system-config.functions"
import { getAvailableSystemInvoiceItemsQueryOptions } from "~/modules/system-config/system-config.queries"
import type { SystemInvoiceItem } from "~/modules/system-config/system-config.service"
import { ComponentErrorBoundary } from "~/root-components/component-error-boundary"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/_admin-layout/system-config/invoice-items",
)({
  component: SystemConfigInvoiceItems,
})

const columnHelper = createColumnHelper<SystemInvoiceItem>()
const TABLE_VISIBLE_COLUMNS_KEY = "system-config-invoice-items-visible-columns"

function SystemConfigInvoiceItems() {
  const toastId = useId()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedInvoiceItem, setSelectedInvoiceItem] =
    useState<SystemInvoiceItem | null>(null)
  const [visibleColumns, setVisibleColumns] = useLocalStorage<VisibilityState>(
    TABLE_VISIBLE_COLUMNS_KEY,
    {},
  )

  const { data: invoiceItems = [], isLoading } = useQuery(
    getAvailableSystemInvoiceItemsQueryOptions(["active", "inactive"]),
  )

  const updateInvoiceItemStatus = useServerFn(updateSystemInvoiceItemStatusFn)

  const updateStatusMutation = useMutation({
    meta: {
      invalidates: [getAvailableSystemInvoiceItemsQueryOptions().queryKey],
    },
    mutationFn: updateInvoiceItemStatus,
    onSuccess: (result) => {
      if (result?.success) {
        toast.success("Invoice item status updated.", { id: toastId })
      } else {
        toast.error(result?.message ?? "Failed to update the status", {
          id: toastId,
        })
      }
    },
    onError: (error) => {
      toast.error(error.message, { id: toastId })
    },
  })

  const getActionsGroups = useStableCallback(
    (item: SystemInvoiceItem): ActionsDropdownMenuGroup[] => [
      {
        actions: [
          {
            label: "Edit Item",
            icon: PencilIcon,
            onClick: () => {
              setSelectedInvoiceItem(item)
              setShowEditModal(true)
            },
          },
        ],
      },
      {
        actions: [
          {
            label: item.status === "active" ? "Disable Item" : "Enable Item",
            icon: PowerIcon,
            onClick: () => {
              toast.loading("Updating status...", { id: toastId })
              updateStatusMutation.mutate({
                data: {
                  id: item.id,
                  status: item.status === "active" ? "inactive" : "active",
                },
              })
            },
            variant:
              item.status === "active"
                ? ("destructive" as const)
                : ("success" as const),
          },
        ],
      },
    ],
  )

  // biome-ignore lint/suspicious/noExplicitAny: please
  const columns: ColumnDef<SystemInvoiceItem, any>[] = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="min-w-[120px]">Name</div>,
        cell: (info) => <span className="text-white">{info.getValue()}</span>,
        meta: { columnName: "Name" },
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: () => <div className="min-w-[200px]">Description</div>,
        cell: (info) => (
          <span className="line-clamp-2 text-white/70">{info.getValue()}</span>
        ),
        meta: { columnName: "Description" },
      }),
      columnHelper.accessor("type", {
        id: "type",
        header: () => <div className="w-[120px]">Type</div>,
        cell: (info) => {
          let label = ""
          let className = ""

          switch (info.getValue()) {
            case "time_based":
              label = "Time-based"
              className = "bg-blue-500/20 text-blue-400"
              break
            case "fixed_fee":
              label = "Fixed Fee"
              className = "bg-purple-500/20 text-purple-400"
              break
            case "variable":
              label = "Variable"
              className = "bg-orange-500/20 text-orange-400"
              break
          }

          return (
            <Badge variant="outline" className={`text-xs ${className}`}>
              {label}
            </Badge>
          )
        },
        meta: { columnName: "Type" },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="w-[100px]">Status</div>,
        cell: (info) => (
          <Badge
            variant="outline"
            className={`text-xs ${
              info.getValue() === "active"
                ? "bg-green-500/20 text-green-400"
                : "bg-gray-500/20 text-gray-400"
            }`}
          >
            {info.getValue() === "active" ? "Active" : "Disabled"}
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          filterSelectOptions: [
            { label: "Active", value: "active" },
            { label: "Disabled", value: "inactive" },
          ],
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="grow text-right">Actions</div>,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <ActionsDropdownMenu groups={getActionsGroups(row.original)} />
          </div>
        ),
      }),
    ],
    [getActionsGroups],
  )

  return (
    <PageDetailsPanel
      title="Invoice Items"
      actions={
        <Button onClick={() => setShowAddModal(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Invoice Item
        </Button>
      }
    >
      <div className="flex-1 overflow-auto p-3">
        <DataTable
          data={invoiceItems}
          columns={columns}
          searchPlaceholder="Search invoice items..."
          noResultsMessage={isLoading ? "Loading..." : "No items found"}
          manualPagination={false}
          manualSorting={false}
          manualFiltering={false}
          getInitialColumnVisibility={() => visibleColumns}
          onColumnVisibilityChange={setVisibleColumns}
        />
      </div>

      <ClientOnly>
        <ComponentErrorBoundary>
          <AddSystemInvoiceItemModal
            open={showAddModal}
            onOpenChange={setShowAddModal}
          />

          {selectedInvoiceItem && (
            <EditSystemInvoiceItemModal
              key={selectedInvoiceItem.id}
              open={showEditModal}
              onOpenChange={setShowEditModal}
              invoiceItem={selectedInvoiceItem}
            />
          )}
        </ComponentErrorBoundary>
      </ClientOnly>
    </PageDetailsPanel>
  )
}
